import { Link } from "react-router-dom";
import { Heart, UserCircle, ArrowUpRight } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";
import { useLang } from "../../i18n";
import Chapter from "./Chapter";
import Reveal from "./Reveal";
import ScrollStrip from "./ScrollStrip";

const CLASS_ORDER = [
    "Tim Hijau (Pemula)",
    "Tim Kuning",
    "Tim Jingga",
    "Tim Biru",
    "Tim Pink",
    "Tim Merah",
    "Speed",
    "Slalom",
];

export default function Atlet({ athletes, topSupported = [] }) {
    const { t } = useLang();

    const groupMap = {};
    const groups = [];
    athletes.forEach((a) => {
        const key = (a.class_name || "").trim();
        if (!groupMap[key]) {
            groupMap[key] = { name: key, items: [] };
            groups.push(groupMap[key]);
        }
        groupMap[key].items.push(a);
    });
    groups.sort((x, y) => {
        const xi = CLASS_ORDER.indexOf(x.name);
        const yi = CLASS_ORDER.indexOf(y.name);
        return (xi === -1 ? 99 : xi) - (yi === -1 ? 99 : yi);
    });

    return (
        <section id="atlet" className="py-24 md:py-32 border-t border-racing-line bg-[#FBF9F5]" data-testid="atlet-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <Chapter number="02" title={t.ch_atlet_t} subtitle={t.ch_atlet_s} />

                {athletes.length === 0 ? (
                    <p className="text-racing-muted" data-testid="atlet-empty">{t.atlet_empty}</p>
                ) : (
                    <>
                        {topSupported.length > 0 && (
                            <Reveal className="mb-14">
                                <div className="border border-racing-line bg-white p-6 md:p-8" data-testid="top-supported-board">
                                    <div className="flex flex-wrap items-center gap-3 mb-5">
                                        <Heart size={20} weight="fill" className="text-racing-red" />
                                        <h3 className="font-display uppercase text-xl md:text-2xl text-neutral-900">{t.top_supported_t}</h3>
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">{t.top_supported_s}</span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-racing-line border border-racing-line">
                                        {topSupported.map((a, i) => (
                                            <Link
                                                key={a.id}
                                                to={`/atlet/${a.id}`}
                                                data-testid={`top-supported-${i}`}
                                                className="group bg-white p-4 flex items-center gap-3 hover:bg-racing-surface transition-colors duration-300"
                                            >
                                                <span className="font-display text-2xl text-racing-golddeep/40 shrink-0">{i + 1}</span>
                                                {a.photo_url ? (
                                                    <img src={fileUrl(a.photo_url)} alt={a.name} className="w-10 h-10 object-cover border border-racing-line shrink-0" />
                                                ) : (
                                                    <div className="w-10 h-10 flex items-center justify-center bg-racing-surface border border-racing-line shrink-0">
                                                        <UserCircle size={20} weight="duotone" className="text-neutral-900/20" />
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate group-hover:text-racing-red transition-colors duration-300">{a.name}</p>
                                                    <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted flex items-center gap-1">
                                                        <Heart size={10} weight="fill" className="text-racing-red" /> {a.likes} Love · {a.comments} Komentar
                                                    </p>
                                                    {a.funds > 0 && (
                                                        <p className="mt-1 inline-block bg-racing-gold/15 border border-racing-gold text-[10px] uppercase tracking-[0.12em] text-racing-golddeep px-2 py-0.5" data-testid={`top-supported-funds-${i}`}>
                                                            Rp {a.funds.toLocaleString("id-ID")}
                                                        </p>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                        )}
                        <div className="space-y-12">
                            {groups.map((group, gi) => (
                                <div key={group.name || "other"} data-testid={`atlet-class-group-${gi}`}>
                                    <Reveal className="flex items-baseline gap-4 mb-5">
                                        <h3 className="font-display uppercase text-2xl md:text-3xl text-neutral-900" data-testid={`atlet-class-title-${gi}`}>
                                            {group.name || t.atlet_class_other}
                                        </h3>
                                        <span className="text-[10px] uppercase tracking-[0.25em] text-racing-golddeep">
                                            {group.items.length} {t.rank_athlete}
                                        </span>
                                    </Reveal>
                                    <Reveal>
                                        <ScrollStrip testid={gi === 0 ? "atlet-list" : `atlet-list-${gi}`}>
                                            {group.items.map((a, i) => (
                                                <Link
                                                    key={a.id}
                                                    to={`/atlet/${a.id}`}
                                                    data-testid={`atlet-row-${gi}-${i}`}
                                                    className="group relative block w-24 md:w-28 shrink-0 snap-start overflow-hidden border border-racing-line bg-racing-surface"
                                                >
                                                    <div className="relative aspect-[3/4] overflow-hidden">
                                                        {a.photo_url ? (
                                                            <img
                                                                src={fileUrl(a.photo_url)}
                                                                alt={a.name}
                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center bg-[#F1ECE3]">
                                                                <UserCircle size={28} weight="duotone" className="text-neutral-900/15" />
                                                            </div>
                                                        )}
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                                                        <ArrowUpRight
                                                            size={12}
                                                            weight="bold"
                                                            className="absolute top-2 right-2 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                        />
                                                        <div className="absolute bottom-0 left-0 right-0 p-2">
                                                            <p className="font-display uppercase text-sm leading-tight text-white truncate">
                                                                {a.name}
                                                            </p>
                                                            <p className="mt-0.5 text-[8px] uppercase tracking-[0.15em] text-white/70 truncate">
                                                                {t.atlet_born} {a.birth_year} · {t.atlet_since} {a.joined_year}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </ScrollStrip>
                                    </Reveal>
                                </div>
                            ))}
                        </div>
                        <Reveal delay={0.1} className="mt-8">
                            <Link
                                to="/atlet"
                                data-testid="atlet-more-link"
                                className="inline-flex items-center gap-3 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white text-xs uppercase tracking-[0.25em] px-8 py-4 transition-colors duration-300"
                            >
                                {t.atlet_more}
                                <ArrowUpRight size={16} weight="bold" />
                            </Link>
                        </Reveal>
                    </>
                )}
            </div>
        </section>
    );
}
